/* Phase 10.7 · Catalog Gap Remediation Report */
import React, { useCallback, useEffect, useState, useMemo } from "react";
import "./AdminMusicalIntelligenceCanary.css";

const API = process.env.REACT_APP_BACKEND_URL;

const COVERAGE_META = {
  green:  { emoji: "🟢", label: "Well covered (≥10)",   color: "#0e7a2c" },
  yellow: { emoji: "🟡", label: "Under-served (3-9)",   color: "#8a5a00" },
  red:    { emoji: "🔴", label: "Gap (<3)",             color: "#a63232" },
};


function fmtPct(v) {
  if (v == null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}
function fmtNum(v) {
  if (v == null) return "—";
  return typeof v === "number" ? v.toFixed(2) : String(v);
}


export default function AdminCatalogGaps() {
  const [report, setReport] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState({ tradition: "all", status: "all" });

  const loadLatest = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [latest, list] = await Promise.all([
        fetch(`${API}/api/admin/musical-intelligence/catalog-gaps/latest`,
              { credentials: "include" }),
        fetch(`${API}/api/admin/musical-intelligence/catalog-gaps/list?limit=10`,
              { credentials: "include" }),
      ]);
      if (latest.ok) {
        const j = await latest.json();
        setReport(j && j.tradition_reports ? j : null);
      }
      if (list.ok) {
        const j = await list.json();
        setRuns(j.reports || []);
      }
    } catch (e) { setErr(String(e.message)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const generate = async () => {
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${API}/api/admin/musical-intelligence/catalog-gaps/generate`,
        { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setReport(j);
      await loadLatest();
    } catch (e) { setErr(String(e.message)); }
    finally { setLoading(false); }
  };

  const filteredTraditions = useMemo(() => {
    if (!report) return [];
    return report.tradition_reports.filter((t) => {
      if (filter.tradition !== "all" && t.scope?.report_id !== filter.tradition) return false;
      if (filter.status !== "all") {
        const has = (t.role_rows || []).some((r) => r.coverage_status === filter.status);
        if (!has) return false;
      }
      return true;
    });
  }, [report, filter]);

  return (
    <div className="mi-canary" data-testid="admin-catalog-gaps">
      <header className="mi-canary-header">
        <div>
          <h1>Catalog Gap Remediation Report</h1>
          <p className="mi-canary-sub">
            Phase 10.7 · Deterministic per-tradition coverage of every applicable
            liturgical role. Six metrics per role. No new repertoire is added
            until this report identifies exactly where it's needed.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {report && (
            <>
              <a className="mi-run"
                 data-testid="catalog-gap-download-json"
                 href={`${API}/api/admin/musical-intelligence/catalog-gaps/${report.id}/export?format=json`}
                 style={{ textDecoration: "none", background: "#22343f" }}>
                Download JSON
              </a>
              <a className="mi-run"
                 data-testid="catalog-gap-download-csv"
                 href={`${API}/api/admin/musical-intelligence/catalog-gaps/${report.id}/export?format=csv`}
                 style={{ textDecoration: "none", background: "#22343f" }}>
                Download CSV
              </a>
            </>
          )}
          <button className="mi-run"
                  data-testid="catalog-gap-generate-btn"
                  disabled={loading}
                  onClick={generate}>
            {loading ? "Generating…" : "Generate fresh report"}
          </button>
        </div>
      </header>

      {err && <div className="mi-canary-error" data-testid="catalog-gap-error">{err}</div>}

      {!report && !loading && (
        <section className="mi-canary-panel" data-testid="catalog-gap-empty">
          <h2>No report yet</h2>
          <p>Click <em>Generate fresh report</em> to compute the first Phase 10.7 catalog-gap
             report. The report is deterministic and does not consume LLM credits.</p>
        </section>
      )}

      {report && (
        <>
          {/* Totals card */}
          <section className="mi-canary-panel" data-testid="catalog-gap-totals">
            <h2>Coverage totals · {new Date(report.generated_at).toLocaleString()}</h2>
            <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                          gap: 12, marginTop: 6 }}>
              <div><span>Traditions</span>
                   <strong data-testid="cg-traditions">{report.totals.traditions}</strong></div>
              <div><span>Applicable roles</span>
                   <strong data-testid="cg-applicable-roles">{report.totals.applicable_roles}</strong></div>
              <div><span>🟢 Green</span>
                   <strong style={{ color: "#0e7a2c" }}
                            data-testid="cg-total-green">{report.totals.green}</strong></div>
              <div><span>🟡 Yellow</span>
                   <strong style={{ color: "#8a5a00" }}
                            data-testid="cg-total-yellow">{report.totals.yellow}</strong></div>
              <div><span>🔴 Red</span>
                   <strong style={{ color: "#a63232" }}
                            data-testid="cg-total-red">{report.totals.red}</strong></div>
              <div><span>Coverage score</span>
                   <strong data-testid="cg-coverage-score">
                     {(report.totals.coverage_score * 100).toFixed(1)}%
                   </strong></div>
            </div>
            {report.totals.needs_canary_run_for.length > 0 && (
              <p style={{ marginTop: 10, fontSize: 12, color: "#8a5a00" }}
                 data-testid="cg-needs-canary">
                ⚠ Engine metrics unavailable for: {report.totals.needs_canary_run_for.join(", ")}
                 &nbsp;— run a canary for these traditions to populate fallback / duplicate / confidence.
              </p>
            )}
          </section>

          {/* Filters */}
          <section className="mi-canary-panel" data-testid="catalog-gap-filters">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <label style={{ fontSize: 12, color: "#333" }}>Tradition</label>
              <select className="mi-canary-input"
                      data-testid="cg-filter-tradition"
                      value={filter.tradition}
                      onChange={(e) => setFilter({ ...filter, tradition: e.target.value })}>
                <option value="all">All traditions</option>
                {report.tradition_reports.map((t) =>
                  <option key={t.scope.report_id} value={t.scope.report_id}>
                    {t.scope.display_name}
                  </option>)}
              </select>
              <label style={{ fontSize: 12, color: "#333" }}>Coverage</label>
              <select className="mi-canary-input"
                      data-testid="cg-filter-status"
                      value={filter.status}
                      onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
                <option value="all">All statuses</option>
                <option value="green">🟢 Green</option>
                <option value="yellow">🟡 Yellow</option>
                <option value="red">🔴 Red (gap)</option>
              </select>
            </div>
          </section>

          {/* Per-tradition cards */}
          {filteredTraditions.map((t) => (
            <section key={t.scope.report_id}
                     className="mi-canary-panel"
                     data-testid={`cg-tradition-${t.scope.report_id}`}
                     style={{ borderLeft:
                              `4px solid ${(t.coverage_summary?.red ?? 0) > 0 ? "#a63232" : "#0e7a2c"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                             alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <h2 style={{ margin: 0 }}>{t.scope.display_name}</h2>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    profile: <code>{t.profile_id}</code>
                    {" · "}canary: <b>{t.canary_available ? "available" : "N/A"}</b>
                  </div>
                </div>
                {t.coverage_summary && (
                  <div style={{ fontSize: 13, color: "#333" }}>
                    <span>🟢 {t.coverage_summary.green}</span>{" · "}
                    <span>🟡 {t.coverage_summary.yellow}</span>{" · "}
                    <span>🔴 {t.coverage_summary.red}</span>{" · "}
                    <b>{(t.coverage_summary.coverage_score * 100).toFixed(0)}%</b>
                  </div>
                )}
              </div>
              {t.error && <p style={{ color: "#a63232" }}>{t.error}</p>}
              {t.role_rows && (
                <table className="mi-canary-table"
                       data-testid={`cg-role-table-${t.scope.report_id}`}
                       style={{ marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th>Role</th><th>Applicability</th><th>Repertoire</th>
                      <th>Coverage</th><th>Fallback</th><th>Duplicate</th>
                      <th>Avg. confidence</th><th>Tier-1</th>
                      <th>Verified match</th><th>Sample</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.role_rows.map((r) => {
                      const meta = COVERAGE_META[r.coverage_status];
                      return (
                        <tr key={r.role_id}
                            data-testid={`cg-role-${t.scope.report_id}-${r.role_id}`}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{r.display_name}</div>
                            <div style={{ fontSize: 11, color: "#666" }}>
                              <code>{r.role_id}</code>
                            </div>
                          </td>
                          <td>{r.applicability}</td>
                          <td style={{ fontSize: 20, fontWeight: 700 }}>
                            {r.canonical_repertoire_count}
                          </td>
                          <td style={{ color: meta?.color }}>
                            {meta?.emoji} {meta?.label}
                          </td>
                          <td>{fmtPct(r.fallback_rate)}</td>
                          <td>{fmtPct(r.duplicate_rate)}</td>
                          <td>{fmtNum(r.average_confidence)}</td>
                          <td>{fmtPct(r.tier1_share)}</td>
                          <td>{fmtPct(r.verified_tier_share)}</td>
                          <td style={{ fontSize: 11, color: "#666" }}>
                            {(r.sample_titles || []).slice(0, 2).join(" · ")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>
          ))}

          {/* Recent reports */}
          {runs.length > 1 && (
            <section className="mi-canary-panel" data-testid="cg-history">
              <h2>Recent reports</h2>
              <table className="mi-canary-table">
                <thead>
                  <tr><th>Generated</th><th>Coverage score</th><th>Green</th>
                      <th>Red</th><th>Trigger</th></tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} data-testid={`cg-history-${r.id}`}>
                      <td>{new Date(r.generated_at).toLocaleString()}</td>
                      <td>{((r.totals?.coverage_score || 0) * 100).toFixed(1)}%</td>
                      <td style={{ color: "#0e7a2c" }}>{r.totals?.green}</td>
                      <td style={{ color: "#a63232" }}>{r.totals?.red}</td>
                      <td style={{ fontSize: 11, color: "#666" }}>{r.triggered_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}
